import { FCC, TextLink, ToastStyleProps, isEmail, useDebounce } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { contactService } from "../../shared/api/contactService";

interface Props {
	email: string;
}

export const ContactExistsNotice: FC<Props> = ({ email }) => {
	const { t } = useTranslation();
	const debouncedEmail = useDebounce(email, 500);

	const { data: notice } = useQuery({
		enabled: isEmail(debouncedEmail),
		queryKey: contactService.keys.checkEmail(debouncedEmail),
		queryFn: () => contactService.checkEmail(debouncedEmail),
	});

	if (!notice) {
		return;
	}

	if (notice.contactEmailState === "IS_ONLY_ON_OTHER_TENANTS") {
		return (
			<Notice title={t("Email is already in use")}>
				{t("Please contact an administrator to gain access to this contact.")}
			</Notice>
		);
	}
	if (notice.contactEmailState === "LINK_TO_LIST") {
		return (
			<Notice title={t("Contact exists already")}>
				{t("The contact using this email address can be found")}{" "}
				<StyledTextLink to={`${notice.id}`}>{t("here")}</StyledTextLink>.
			</Notice>
		);
	}
	return (
		<Notice title={t("Contact is archived")}>
			{t("The contact using this email address can be found")}{" "}
			<StyledTextLink to={`/contacts/archived/${notice.id}`}>{t("here")}</StyledTextLink>.
		</Notice>
	);
};
const Notice: FCC<{ title: string }> = ({ title, children }) => {
	return (
		<NoticeContainer $type="warning">
			<Header>
				<Title>{title}</Title>
			</Header>
			<Body>{children}</Body>
		</NoticeContainer>
	);
};

const StyledTextLink = styled(TextLink)`
	display: inline-flex;
	text-decoration: underline;
`;

const NoticeContainer = styled.div<ToastStyleProps>`
	${({ theme }) => theme.toast.container};
	width: 100%;
	max-width: unset;
	margin-bottom: ${({ theme }) => theme.spacing(8)};
`;

const Header = styled.div`
	${({ theme }) => theme.toast.header};
`;

const Title = styled.div`
	${({ theme }) => theme.toast.title};
`;

const Body = styled.div`
	${({ theme }) => theme.toast.body};
`;
